import React, { useMemo, useState } from "react";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { ro } from "date-fns/locale";
import { format, parseISO, addDays, startOfWeek, isValid } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

/**
 * M870 — Modern date picker for the Grupă nouă / Editează grupă modal.
 *
 * Wraps `react-day-picker` (already shipped with the codebase) inside a
 * Radix Popover styled to match the platform's purple accent. Stores ISO
 * `YYYY-MM-DD` strings in form state (same payload as the previous native
 * `<input type="date">`) and displays a friendly `dd/MM/yyyy` value.
 *
 * Why inline here (not in `components/ui/`): the FPED-purple theme + RO
 * weekend differentiation + planning-shortcuts row are specific to this
 * admin form; we don't want them leaking into a generic primitive.
 */
export default function DatePickerField({
  value, onChange, required = false, minDate, contextDate, testid, size = "md", ariaLabel,
}) {
  const [open, setOpen] = useState(false);

  const selected = useMemo(() => {
    if (!value) return undefined;
    const d = parseISO(value);
    return isValid(d) ? d : undefined;
  }, [value]);

  const display = selected ? format(selected, "dd/MM/yyyy") : "";

  const minDateObj = useMemo(() => {
    if (!minDate) return undefined;
    const d = parseISO(minDate);
    return isValid(d) ? d : undefined;
  }, [minDate]);

  // M871 — when there's no value yet, lean on the contextual hint (typically
  // the start_date of the same form) so the operator lands on the right month
  // immediately instead of today's month. Falls back to today.
  const contextDateObj = useMemo(() => {
    if (!contextDate) return undefined;
    const d = parseISO(contextDate);
    return isValid(d) ? d : undefined;
  }, [contextDate]);

  const today = new Date();
  const defaultMonth = selected || contextDateObj || today;

  const commit = (date) => {
    if (!date) return;
    onChange(format(date, "yyyy-MM-dd"));
    setOpen(false);
  };

  const shortcuts = useMemo(() => {
    // When a min date is enforced (typical end-date scenario), anchor the
    // shortcuts to that minimum so "+7 zile" means "+7 days after the start"
    // instead of "+7 days from today" — which would often produce a disabled date.
    const anchor = minDateObj || today;
    return [
      { label: "Azi", date: today },
      { label: "+7 zile", date: addDays(anchor, 7) },
      { label: "Săpt. viitoare", date: startOfWeek(addDays(anchor, 7), { weekStartsOn: 1 }) },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minDateObj?.getTime()]);

  const currentYear = today.getFullYear();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          aria-required={required}
          aria-label={ariaLabel}
          className={`group w-full flex items-center justify-between gap-2 bg-white border border-[#E5E7EB] ${size === "sm" ? "rounded-lg px-2.5 py-1.5" : "rounded-xl px-3 py-2.5"} text-left text-[#373A45] hover:border-[#965EA7]/50 focus:ring-2 focus:ring-[#965EA7] focus:border-transparent outline-none transition-colors ${
            !display ? "text-[#9CA3AF]" : ""
          } ${open ? "ring-2 ring-[#965EA7] border-transparent" : ""}`}
        >
          <span className="truncate text-sm">
            {display || "dd/mm/yyyy"}
          </span>
          <CalendarIcon
            size={16}
            className={`shrink-0 transition-colors ${open ? "text-[#965EA7]" : "text-[#6B7280] group-hover:text-[#965EA7]"}`}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="z-[70] w-auto p-0 rounded-2xl border-[#E5E7EB] shadow-xl overflow-hidden"
        data-testid={testid ? `${testid}-popover` : undefined}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DayPicker
          mode="single"
          selected={selected}
          defaultMonth={defaultMonth}
          onSelect={commit}
          locale={ro}
          weekStartsOn={1}
          showOutsideDays
          captionLayout="dropdown-buttons"
          fromYear={currentYear - 1}
          toYear={currentYear + 3}
          disabled={minDateObj ? { before: minDateObj } : undefined}
          modifiers={{
            weekend: { dayOfWeek: [0, 6] },
          }}
          modifiersClassNames={{
            weekend: "text-[#965EA7]/70",
          }}
          className="p-3"
          classNames={{
            months: "flex flex-col",
            month: "space-y-3",
            caption: "flex items-center justify-between px-1 pt-1 pb-2",
            caption_label: "hidden",
            caption_dropdowns: "flex items-center gap-1.5 text-sm font-semibold text-[#373A45]",
            dropdown:
              "appearance-none bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg px-2 py-1 text-sm font-semibold text-[#373A45] hover:border-[#965EA7]/40 focus:ring-2 focus:ring-[#965EA7] focus:border-transparent outline-none cursor-pointer pr-6 bg-no-repeat bg-right",
            dropdown_month: "",
            dropdown_year: "",
            dropdown_icon: "hidden",
            vhidden: "sr-only",
            nav: "flex items-center gap-1",
            nav_button:
              "inline-flex items-center justify-center h-7 w-7 rounded-lg text-[#6B7280] hover:bg-[#F0E7F4] hover:text-[#7A4986] transition-colors",
            nav_button_previous: "",
            nav_button_next: "",
            table: "w-full border-collapse",
            head_row: "flex",
            head_cell:
              "w-9 text-[11px] uppercase tracking-wider font-semibold text-[#9CA3AF] py-1 text-center",
            row: "flex w-full mt-1",
            cell: "p-0 w-9 h-9 text-center text-sm relative",
            day: "h-9 w-9 inline-flex items-center justify-center rounded-lg text-sm font-medium text-[#373A45] hover:bg-[#F0E7F4] hover:text-[#7A4986] transition-colors cursor-pointer aria-selected:opacity-100",
            day_selected:
              "!bg-[#965EA7] !text-white hover:!bg-[#7A4986] focus:!bg-[#7A4986]",
            day_today: "ring-1 ring-inset ring-[#965EA7]/40 font-bold",
            day_outside: "text-[#D1D5DB]",
            day_disabled: "text-[#D1D5DB] opacity-50 cursor-not-allowed hover:bg-transparent hover:text-[#D1D5DB]",
            day_hidden: "invisible",
          }}
          components={{
            IconLeft: () => <ChevronLeft size={16} />,
            IconRight: () => <ChevronRight size={16} />,
          }}
        />
        <div className="flex items-center gap-1.5 px-3 py-2.5 border-t border-[#E5E7EB] bg-[#FAFAFB]">
          {shortcuts.map((s) => (
            <button
              key={s.label}
              type="button"
              onClick={() => commit(s.date)}
              className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-[#E5E7EB] bg-white text-[#373A45] hover:border-[#965EA7]/40 hover:bg-[#F0E7F4] hover:text-[#7A4986] transition-colors"
              data-testid={testid ? `${testid}-shortcut-${s.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}` : undefined}
            >
              {s.label}
            </button>
          ))}
          {value && (
            <button
              type="button"
              onClick={() => { onChange(""); setOpen(false); }}
              className="ml-auto text-[11px] font-semibold px-2.5 py-1 rounded-full text-[#6B7280] hover:text-[#E63946] transition-colors"
              data-testid={testid ? `${testid}-clear` : undefined}
            >
              Șterge
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
